import React, { useEffect, useState } from "react";
import { api, fmtPLN, fmtDate, fmtDateOnly } from "@/lib/api";
import { useAuth } from "@/lib/auth-context";
import { KPIStatCard } from "@/components/KPIStatCard";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Button } from "@/components/ui/button";
import { StatusBadge } from "@/components/StatusBadge";
import { ClipboardCheck, Receipt, Bell, Check, X as XIcon } from "lucide-react";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";

export default function ManagerDashboard() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  async function load() {
    setLoading(true);
    try {
      const { data } = await api.get("/dashboard/manager");
      setData(data);
    } catch (e) { toast.error("Błąd wczytywania"); }
    finally { setLoading(false); }
  }
  useEffect(() => { load(); }, []);

  async function rejectRecon(id) {
    const reason = prompt("Powód odrzucenia:");
    if (!reason || reason.length < 3) return;
    try {
      await api.post(`/daily-reconciliations/${id}/reject`, { reason });
      toast.success("Odrzucono");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Błąd"); }
  }

  async function approveExpense(id) {
    try {
      await api.post(`/expenses/${id}/approve`);
      toast.success("Wydatek zatwierdzony");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Błąd"); }
  }

  if (loading) return <Skeleton className="h-screen" />;
  if (!data) return null;

  return (
    <div className="space-y-6" data-testid="manager-dashboard">
      <div>
        <h1 className="text-3xl md:text-4xl font-semibold tt-display">Dashboard Managera</h1>
        <p className="text-[var(--tt-muted)] mt-1">Zatwierdzenia, zadania i alerty operacyjne.</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <KPIStatCard label="Do zatwierdzenia" value={data.kpis.pending_approvals_count} icon={ClipboardCheck} testId="kpi-pending-approvals" to="/daily?status=submitted" />
        <KPIStatCard label="Wydatki do zatwierdzenia" value={data.kpis.pending_expenses_count} icon={Receipt} testId="kpi-pending-expenses" to="/expenses?status=submitted" />
        <KPIStatCard label="Otwarte alerty" value={data.kpis.open_alerts_count} icon={Bell} testId="kpi-open-alerts" accent="accent" to="/alerts?status=open" />
      </div>

      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <h2 className="tt-section-title">Rozliczenia dzienne czekają na zatwierdzenie</h2>
          <Button variant="ghost" size="sm" onClick={() => navigate("/daily")}>Zobacz wszystkie</Button>
        </div>
        {data.pending_approvals.length === 0 ? (
          <Card className="tt-card"><CardContent className="p-6 text-center text-[var(--tt-muted)]">Brak zgłoszeń do zatwierdzenia.</CardContent></Card>
        ) : (
          <div className="space-y-3" data-testid="manager-approvals-queue">
            {data.pending_approvals.map((r) => {
              const isOwn = r.created_by_id === user.id;
              return (
                <Card key={r.id} className="tt-card" data-testid="approval-card">
                  <CardContent className="p-4 flex flex-wrap items-center justify-between gap-3">
                    <div className="min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <StatusBadge value="submitted" />
                        <span className="text-xs font-semibold uppercase tracking-wide">{r.department}</span>
                        <span className="text-sm tt-mono">{fmtDateOnly(r.business_date)}</span>
                      </div>
                      <div className="mt-2 text-sm text-[var(--tt-muted)]">
                        Oczekiwane: <span className="tt-mono">{fmtPLN(r.v2_expected_cash ?? r.expected_cash)}</span> • Policzone: <span className="tt-mono">{fmtPLN(r.counted_cash)}</span> • Różnica: <span className={`tt-mono font-semibold ${Math.abs((r.v2_cash_variance ?? r.variance) || 0) > 50 ? "text-[var(--tt-critical-fg)]" : ""}`}>{(r.v2_cash_variance ?? r.variance) > 0 ? "+" : ""}{fmtPLN(r.v2_cash_variance ?? r.variance)}</span>
                      </div>
                    </div>
                    <div className="flex flex-wrap items-center gap-2">
                      {isOwn ? (
                        <span className="text-xs italic text-[var(--tt-muted)] mr-2">To Twoje zgłoszenie</span>
                      ) : null}
                      <Button size="sm" variant="outline" disabled={isOwn} onClick={() => rejectRecon(r.id)} data-testid="approval-reject-button"><XIcon className="h-4 w-4 mr-1" />Odrzuć</Button>
                      <Button size="sm" disabled={isOwn} onClick={() => navigate(`/daily/${r.id}?action=quick-approve`)} data-testid="approval-approve-button"><Check className="h-4 w-4 mr-1" />Sprawdź kasę</Button>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}
      </div>

      <div className="space-y-3">
        <h2 className="tt-section-title">Wydatki do zatwierdzenia</h2>
        {data.pending_expenses.length === 0 ? (
          <Card className="tt-card"><CardContent className="p-6 text-center text-[var(--tt-muted)]">Brak.</CardContent></Card>
        ) : (
          <div className="space-y-3">
            {data.pending_expenses.map((e) => (
              <Card key={e.id} className="tt-card">
                <CardContent className="p-4 flex flex-wrap items-center justify-between gap-3">
                  <div className="min-w-0">
                    <div className="text-xs font-semibold uppercase tracking-wide">{e.department} • {e.category}</div>
                    <div className="font-medium mt-1">{e.description}</div>
                    <div className="text-xs text-[var(--tt-muted)] mt-0.5">Metoda: {e.payment_method} • Dokument: {e.has_document ? "tak" : "brak"}</div>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="text-lg font-semibold tt-mono">{fmtPLN(e.amount)}</div>
                    <Button size="sm" onClick={() => approveExpense(e.id)}><Check className="h-4 w-4 mr-1" />Zatwierdź</Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
